import {
    CONVERSATIONAL_MM_WELCOME_MENU_OPTIONS,
    DEFAULT_WELCOME_OPENER_INTRO,
    parseMultiSelectConfirmedMessage,
} from '@fruitful/llm-service-shared';
import { type ReactNode, useCallback, useRef, useState } from 'react';
import Animated, {
    Easing,
    useAnimatedStyle,
    useSharedValue,
    withTiming,
} from 'react-native-reanimated';
import { LabelRegular, SmoothieScrollView, View, YStack } from 'smoothie';

import { AssistantMessage } from '../shared-chat/AssistantMessage';
import { ChatColumn } from '../shared-chat/ChatColumn';
import { SingleSelectWidget } from '../widgets/SingleSelectWidget';

export interface WelcomeStartMenuOption {
    id: string;
    label: string;
}

/**
 * The onboarding "what best describes you" options. Monopath: every pick — and
 * any free-text answer — routes to the single goals-system flow. The label is
 * carried into the conversation as member-notes context (it does NOT branch);
 * see `OnboardingChatContainer.handleWelcomeStartSelect`.
 *
 * Sourced from `@fruitful/llm-service-shared` so the member-facing menu and the
 * QA simulation stay in lockstep on the exact first-question options.
 */
export const WELCOME_START_MENU_OPTIONS: WelcomeStartMenuOption[] =
    CONVERSATIONAL_MM_WELCOME_MENU_OPTIONS;

/**
 * Opener intro copy — the FIRST line of the partner-question opener (replacing
 * the default "You're in the right place…" line) — keyed by the option ids above.
 *
 * Placeholder: every option currently maps to the default. Drop bespoke copy in
 * per option (1–4) as it arrives; anything left as the default keeps today's
 * text. This also drives `money-goal` deep-links, whose param resolves to one of
 * these ids. Free-text ("Something else…") / unmatched → default.
 */
export const WELCOME_OPENER_INTRO_BY_OPTION: Record<string, string> = {
    'get-out-of-debt': DEFAULT_WELCOME_OPENER_INTRO,
    'create-budget-money-system': DEFAULT_WELCOME_OPENER_INTRO,
    'make-progress-goals': DEFAULT_WELCOME_OPENER_INTRO,
    'optimize-investments': DEFAULT_WELCOME_OPENER_INTRO,
    'not-sure': DEFAULT_WELCOME_OPENER_INTRO,
};

/**
 * Resolves the opener intro line for a welcome selection. Falls back to the
 * default for free-text answers or ids without bespoke copy.
 */
export const resolveWelcomeOpenerIntro = (
    welcomeOptionId: string | undefined,
): string =>
    (welcomeOptionId != null
        ? WELCOME_OPENER_INTRO_BY_OPTION[welcomeOptionId]
        : undefined) ?? DEFAULT_WELCOME_OPENER_INTRO;

/**
 * The deterministic intro the assistant "types" before the menu. Exported so
 * the thread can keep it as the first assistant turn after the member answers
 * (see OnboardingChatContainer), instead of it disappearing with the overlay.
 */
export const WELCOME_INTRO_MESSAGE =
    'Your path to progress begins today. Where do you want to start?';
const MENU_HEADER = 'Where do you want to start?';
const MENU_SUBTEXT =
    'Don\u2019t worry \u2014 we\u2019ll dial this in as we go!';
const CUSTOM_PLACEHOLDER = 'Something else...';
const DISCLOSURE = 'Fruitful onboarding is driven by AI and can make mistakes.';
/** The welcome menu is saved as member-notes context (it does NOT branch). */
const WELCOME_RESPONSE_KEY = 'memberNotes';

const MENU_REVEAL_MS = 260;

const AnimatedView = Animated.createAnimatedComponent(View);

interface WelcomeStartMenuProps {
    /** Rendered above the message (the welcome heading + animation). */
    header?: ReactNode;
    /** Mounts the intro + menu once history restoration confirms a fresh session. */
    showMenu: boolean;
    /** Fired with the member's answer text (option label or free-text) and, for
     *  a menu pick, the selected option id (absent for free-text). */
    onSelect: (displayText: string, optionId?: string) => void;
    /** Fired after the exit fade completes. */
    onExitComplete: () => void;
    /** Extra bottom padding so the widget scrolls above the mobile-web keyboard. */
    keyboardOffset?: number;
}

export const WelcomeStartMenu = ({
    header,
    showMenu,
    onSelect,
    onExitComplete,
    keyboardOffset = 0,
}: WelcomeStartMenuProps) => {
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const [introTyped, setIntroTyped] = useState(false);
    const submittedRef = useRef(false);
    const reveal = useSharedValue(0);
    const exit = useSharedValue(0);

    const handleIntroTyped = useCallback(() => {
        setIntroTyped(true);
        reveal.value = withTiming(1, {
            duration: MENU_REVEAL_MS,
            easing: Easing.out(Easing.cubic),
        });
    }, [reveal]);

    const commit = useCallback(
        (displayText: string, optionId?: string) => {
            const trimmed = displayText.trim();
            if (trimmed === '' || submittedRef.current) return;
            submittedRef.current = true;
            setHasSubmitted(true);
            onSelect(trimmed, optionId);
            // Other single-select menus don't fade out — they just disappear the
            // moment they're answered. Match that: snap the overlay away and hand
            // off immediately instead of running an exit fade.
            exit.value = 1;
            onExitComplete();
        },
        [exit, onSelect, onExitComplete],
    );

    // The footer widget builds its own [multi-select-confirmed] payload; we only
    // need the display text and the tapped option id (absent for free text) to
    // route into the welcome branch. Custom text arrives as displayText with no
    // selected option id, which resolves to the default opener intro.
    const handleWidgetSubmit = useCallback(
        (displayText: string, agentPayload: string) => {
            const parsed = parseMultiSelectConfirmedMessage(agentPayload);
            commit(displayText, parsed?.selectedOptionIds[0]);
        },
        [commit],
    );

    const menuStyle = useAnimatedStyle(() => {
        const enter = reveal.value;
        const leave = 1 - exit.value;
        return {
            // Enter: fade + slight rise into place. Exit: clean fade only — no
            // downward drift, which read as sluggish.
            opacity: enter * leave,
            transform: [{ translateY: (1 - enter) * 8 }],
        };
    });

    return (
        <YStack flex={1} width="100%">
            {/* Heading + intro live in the scrollable body… */}
            <SmoothieScrollView
                style={{ flex: 1, width: '100%' }}
                contentContainerStyle={{
                    flexGrow: 1,
                    justifyContent: 'flex-start',
                }}
                containerStyle={{
                    marginHorizontal: 0,
                    width: '100%',
                    alignSelf: 'stretch',
                }}
                showsVerticalScrollIndicator={false}
            >
                <YStack width="100%" alignItems="center">
                    <ChatColumn width="100%">
                        {header}
                        {showMenu && (
                            <YStack paddingTop={24}>
                                <AssistantMessage
                                    content={WELCOME_INTRO_MESSAGE}
                                    showLogoIcon={false}
                                    onTypingComplete={handleIntroTyped}
                                />
                            </YStack>
                        )}
                    </ChatColumn>
                </YStack>
            </SmoothieScrollView>

            {/* …while the menu docks at the bottom like every other single-select. */}
            {showMenu && introTyped && (
                <AnimatedView
                    style={[menuStyle, { marginBottom: keyboardOffset }]}
                >
                    <YStack width="100%" alignItems="center">
                        <ChatColumn width="100%">
                            <YStack
                                width="100%"
                                paddingHorizontal="$4"
                                alignItems="center"
                            >
                                <SingleSelectWidget
                                    options={WELCOME_START_MENU_OPTIONS}
                                    responseKey={WELCOME_RESPONSE_KEY}
                                    maxSelections={1}
                                    allowCustomInput
                                    customInputPlaceholder={CUSTOM_PLACEHOLDER}
                                    promptText={MENU_HEADER}
                                    promptSubtext={MENU_SUBTEXT}
                                    disabled={hasSubmitted}
                                    isActive={!hasSubmitted}
                                    onSubmit={handleWidgetSubmit}
                                />
                            </YStack>
                            <LabelRegular
                                color="$black"
                                textAlign="center"
                                paddingTop="$3"
                                paddingBottom="$3"
                            >
                                {DISCLOSURE}
                            </LabelRegular>
                        </ChatColumn>
                    </YStack>
                </AnimatedView>
            )}
        </YStack>
    );
};
